import React from 'react';
import { arrayOf, shape, string, number, bool, func, array } from 'prop-types';
import styled from 'styled-components';

import { Pagination, Icon, Dropdown, Header, Table } from 'semantic-ui-react';
import HeaderCell from './HeaderCell';
import Avatar from '~/views/common/Avatar';

import mapping from '~/utils/mapping.json';


const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin: 1rem 0;
    width: 100%;
`;

const WrapperName = styled.div`
    display: flex;
    align-items: center;
`;

const Menu = styled.div`
    display: flex;
    align-items: flex-start;
    border-bottom: 2px solid rgba(34,36,38,.15);
    padding: 0 4px;
`;

const StyledHeader = styled(Header).attrs({ as: 'h4' })`
    &.ui.header {
        margin-right: auto;
        margin-bottom: 0.65rem;
        text-transform: capitalize;
    }
`;

const ActivePage = styled.div`
    font-weight: bold;
    margin-left: 1rem;
    padding-left: 1rem;
    border-left: 1px solid rgba(34,36,38,.15);
`;

const StyledPagination = styled(Pagination)`
    &.ui.pagination {
        align-self: center;
        margin-top: 1rem;
    }
`;

const Name = styled.span`
    white-space: pre;
`;

const StyledRow = styled(Table.Row)`
    cursor: ${props => (props.positive ? 'initial' : 'pointer')};
`;


export const NoResultsFound = () => (
    <Container>
        <Menu>
            <StyledHeader>
                No Results Found
            </StyledHeader>
        </Menu>
    </Container>
);

const SearchResults = ({
    numResults,
    numPerPage,
    numPages,
    activePage,
    pageData,
    handlePerPageChange,
    handlePageChange,
    sortByField,
    isSortReverse,
    handleSortChange,
    setItemSelected,
    selectedItems,
}) => {
    const onHandleItemClick = (item) => {
        if (selectedItems && item) {
            const elementSelected = selectedItems.find(e => e.id === item.id);
            if (!elementSelected) {
                const newElements = [...selectedItems];
                newElements.push(item);
                setItemSelected(newElements);
            }
        }
    };

    return (
        <Container>
            <Menu>
                <StyledHeader>
                    { numResults } { (numResults === 1) ? 'Result' : 'Results' } found
                </StyledHeader>

                <div>
                    <Dropdown
                        inline
                        compact
                        onChange={handlePerPageChange}
                        defaultValue={numPerPage}
                        options={mapping.search.items_per_page}
                    />
                    <span>&nbsp;Items per page</span>
                </div>

                <ActivePage>
                    { activePage }-{ numPages }
                </ActivePage>
            </Menu>

            <Table selectable singleLine>
                <Table.Header>
                    <Table.Row>
                        <HeaderCell
                            field="name"
                            sortByField={sortByField}
                            isSortReverse={isSortReverse}
                            handleSortChange={handleSortChange}
                        />
                        <HeaderCell
                            field="created"
                            sortByField={sortByField}
                            isSortReverse={isSortReverse}
                            handleSortChange={handleSortChange}
                            textAlign="center"
                            collapsing
                        />
                        <HeaderCell
                            field="modified"
                            sortByField={sortByField}
                            isSortReverse={isSortReverse}
                            handleSortChange={handleSortChange}
                            textAlign="center"
                            collapsing
                        />
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    { pageData.map((item) => {
                        const {
                            id,
                            name,
                            logo,
                            created,
                            modified,
                        } = item;
                        const elementSelected = selectedItems.find(e => e.id === id);
                        const selected = !!((elementSelected));
                        return (
                            <StyledRow
                                key={id}
                                onClick={selected ? null : () => onHandleItemClick(item)}
                                positive={selected}
                            >
                                <Table.Cell>
                                    <WrapperName>
                                        <Avatar src={logo} />
                                        <Name>{ name }</Name>
                                    </WrapperName>
                                </Table.Cell>
                                <Table.Cell collapsing textAlign="center">
                                    { created }
                                </Table.Cell>
                                <Table.Cell collapsing textAlign="center">
                                    { modified }
                                </Table.Cell>
                            </StyledRow>
                        );
                    }) }
                </Table.Body>
            </Table>

            { numPages > 1 && (
                <StyledPagination
                    totalPages={numPages}
                    activePage={activePage}
                    onPageChange={handlePageChange}
                    ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
                    firstItem={{ content: <Icon name="angle double left" />, icon: true }}
                    lastItem={{ content: <Icon name="angle double right" />, icon: true }}
                    prevItem={{ content: <Icon name="angle left" />, icon: true }}
                    nextItem={{ content: <Icon name="angle right" />, icon: true }}
                />
            ) }
        </Container>
    );
};

SearchResults.propTypes = {
    pageData: arrayOf(shape({
        id: string.isRequired,
        name: string.isRequired,
        logo: string.isRequired,
        created: string.isRequired,
        modified: string.isRequired,
    })).isRequired,

    numResults: number.isRequired,
    numPerPage: number.isRequired,
    numPages: number.isRequired,
    activePage: number.isRequired,

    handlePerPageChange: func.isRequired,
    handlePageChange: func.isRequired,

    sortByField: string.isRequired,
    isSortReverse: bool.isRequired,
    handleSortChange: func.isRequired,
    setItemSelected: func.isRequired,
    selectedItems: array,
};

export default SearchResults;
